import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/store/auth'
import { useEffectiveClientId } from '@/hooks/useAuth'
import { useProfile } from '@/hooks/useProfile'
import { useRegions, useTopics } from '@/hooks/useConfig'
import { functionError } from '@/hooks/useMix'
import { useWatchState } from '@/hooks/useAlerts'
import { FEED_REFRESH_PARAM, FEED_REFRESH_MIX } from '@/hooks/useControls'
import { SEED_COSTS, InsufficientSeedsError } from '@/hooks/useSeeds'
import { tossSeeds, useSeedFx } from '@/lib/seedFx'
import type {
  FeedData, FeedHeadline, FeedNudge, FeedStory,
} from '@/components/feed/feedTypes'
import { KEEP_DAYS, dayKey } from '@/components/feed/feedText'

/**
 * Your feed's data (Brian's headlines.html v7.1 and desk.html v1.5).
 *
 * Headlines are written by the headlines function; the feed reads them, a
 * person's decisions (× is 'left', Build is 'right'), and the stories built
 * from them. A story writes in the background: build-story inserts it at once
 * as 'developing', so the Writing bar and the Library both see it, and fills
 * it in when Emma is done.
 */

const STORY_COLS = 'id, headline_id, status, build_stage, headline, category, created_at, updated_at, article, script, caption, voice, source_name, source_url, image_url'

export const feedKey = (clientId: string | null | undefined, userId: string | null | undefined) => ['feed', clientId, userId]

/** Everything dealt in the last seven days, with decisions and stories. */
export function useFeedData() {
  const clientId = useEffectiveClientId()
  const userId = useAuthStore((s) => s.user?.id)
  return useQuery({
    queryKey: feedKey(clientId, userId),
    enabled: !!clientId && !!userId,
    // While a story is writing, look again every few seconds.
    refetchInterval: (q) => {
      const d = q.state.data as FeedData | undefined
      return d && [...d.stories.values()].some((s) => s.status === 'developing') ? 3000 : false
    },
    queryFn: async (): Promise<FeedData> => {
      const since = new Date(Date.now() - KEEP_DAYS * 864e5).toISOString()
      const [hl, dec, st] = await Promise.all([
        supabase.from('headlines').select('*').eq('client_id', clientId!)
          .gte('fetched_at', since).order('fetched_at', { ascending: true }).limit(400),
        supabase.from('headline_decisions').select('headline_id, decision')
          .eq('client_id', clientId!).eq('user_id', userId!),
        supabase.from('stories').select(STORY_COLS).eq('client_id', clientId!)
          .not('headline_id', 'is', null).gte('created_at', since),
      ])
      if (hl.error) throw hl.error
      if (dec.error) throw dec.error
      if (st.error) throw st.error
      return {
        headlines: (hl.data ?? []) as FeedHeadline[],
        decided: new Map((dec.data ?? []).map((d) => [d.headline_id as string, d.decision as 'left' | 'right'])),
        stories: new Map(((st.data ?? []) as FeedStory[]).map((s) => [s.headline_id as string, s])),
      }
    },
  })
}

/** "Tuned for Bend + 2 places · 6 topics · 0 watches". */
export function useFeedTuned() {
  const { data: regions } = useRegions()
  const { data: topics } = useTopics()
  const { data: watch } = useWatchState()
  // The home place first, as Places and Feed controls show it.
  const active = (regions ?? []).filter((r) => r.active)
    .sort((a, b) => Number(!!(b as { home?: boolean }).home) - Number(!!(a as { home?: boolean }).home))
  const mix = (topics ?? []).filter((t) => t.share > 0).sort((a, b) => b.share - a.share)
  return {
    ready: !!regions && !!topics,
    home: active[0]?.name ?? null,
    morePlaces: Math.max(0, active.length - 1),
    topics: mix.length,
    mix,
    // Breaking news watches (watches table, 20240037, via watch_state).
    watches: watch?.watches.length ?? 0,
    watchesPaused: !!watch?.paused,
  }
}

/** The first Build asks once, per person (users.build_ok). */
export function useBuildOk() {
  const { data: profile } = useProfile()
  const userId = useAuthStore((s) => s.user?.id)
  const qc = useQueryClient()
  const set = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from('users').update({ build_ok: true }).eq('id', userId!)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['profile', userId] }),
  })
  const ok = !!(profile as { build_ok?: boolean } | null | undefined)?.build_ok
  return { ok, loaded: profile !== undefined, confirm: () => set.mutateAsync() }
}

function patchFeed(qc: ReturnType<typeof useQueryClient>, key: unknown[], fn: (d: FeedData) => FeedData) {
  const prev = qc.getQueryData<FeedData>(key)
  if (prev) qc.setQueryData<FeedData>(key, fn(prev))
  return prev
}

/**
 * Build · 12, in the background. The card leaves the feed at once, the
 * Writing bar shows it, and the story lands in the Library. The 12 seeds are
 * charged by the server once the story is written; the chip ticks now.
 */
export function useStartBuild() {
  const clientId = useEffectiveClientId()
  const userId = useAuthStore((s) => s.user?.id)
  const qc = useQueryClient()
  const key = feedKey(clientId, userId)
  return useMutation({
    mutationFn: async (h: FeedHeadline) => {
      const { error: dErr } = await supabase.from('headline_decisions').insert({
        headline_id: h.id, client_id: clientId!, user_id: userId ?? null, decision: 'right',
      })
      if (dErr && !/duplicate key/i.test(dErr.message)) throw dErr
      const { data, error } = await supabase.functions.invoke('build-story', {
        body: { client_id: clientId, headline_id: h.id, idempotency_key: `build:${h.id}`, background: true },
      })
      if (error) {
        const e = await functionError(error)
        if (e.status === 402 || /insufficient seeds/i.test(e.message)) throw new InsufficientSeedsError(e.message)
        throw e
      }
      return data as { story: FeedStory; reused?: boolean }
    },
    onMutate: async (h) => {
      tossSeeds(SEED_COSTS.build_story)
      await qc.cancelQueries({ queryKey: key })
      const now = new Date().toISOString()
      const prev = patchFeed(qc, key, (d) => {
        const decided = new Map(d.decided); decided.set(h.id, 'right')
        const stories = new Map(d.stories)
        if (!stories.has(h.id)) {
          stories.set(h.id, {
            id: `pending-${h.id}`, headline_id: h.id, status: 'developing', build_stage: 'queued', headline: h.headline,
            category: h.topic ?? 'events', created_at: now, updated_at: now, article: null, script: '', caption: null,
            voice: null, source_name: h.source_name, source_url: h.source_url, image_url: h.image_url ?? null,
          })
        }
        return { ...d, decided, stories }
      })
      return { prev }
    },
    onError: (_e, _h, ctx) => {
      useSeedFx.getState().release()
      if (ctx?.prev) qc.setQueryData(key, ctx.prev)
      // A failed build must not leave the card decided: it goes back in the feed.
      void qc.invalidateQueries({ queryKey: key })
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: key })
      void qc.invalidateQueries({ queryKey: ['stories', clientId] })
      void qc.invalidateQueries({ queryKey: ['library', clientId] })
      void qc.invalidateQueries({ queryKey: ['seed-balance', clientId] })
    },
  })
}

/** ×: Not interested, one tap. Undo deletes the decision. */
export function useDecide() {
  const clientId = useEffectiveClientId()
  const userId = useAuthStore((s) => s.user?.id)
  const qc = useQueryClient()
  const key = feedKey(clientId, userId)
  const pass = useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase.from('headline_decisions').insert(
        ids.map((id) => ({ headline_id: id, client_id: clientId!, user_id: userId ?? null, decision: 'left' })),
      )
      if (error && !/duplicate key/i.test(error.message)) throw error
    },
    onMutate: (ids) => ({ prev: patchFeed(qc, key, (d) => { const m = new Map(d.decided); ids.forEach((id) => m.set(id, 'left')); return { ...d, decided: m } }) }),
    onError: (_e, _ids, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev) },
    onSettled: () => { void qc.invalidateQueries({ queryKey: key }) },
  })
  const undo = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('headline_decisions').delete()
        .eq('headline_id', id).eq('user_id', userId!).eq('decision', 'left')
      if (error) throw error
    },
    onMutate: (id) => ({ prev: patchFeed(qc, key, (d) => { const m = new Map(d.decided); m.delete(id); return { ...d, decided: m } }) }),
    onError: (_e, _id, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev) },
    onSettled: () => { void qc.invalidateQueries({ queryKey: key }) },
  })
  return { pass, undo }
}

/** Fewer like this: a gentle nudge, 15% each, at most three. */
export function useFeedNudges() {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['feed-nudges', clientId],
    enabled: !!clientId,
    queryFn: async () => {
      const { data, error } = await supabase.from('feed_nudges').select('kind, key, label, n')
        .eq('client_id', clientId!).order('updated_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as FeedNudge[]
    },
  })
}

export function useNudge() {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (a: { kind: 'topic' | 'source'; key: string; label: string }) => {
      const { data, error } = await supabase.rpc('feed_nudge', { p_kind: a.kind, p_key: a.key, p_label: a.label })
      if (error) throw error
      return data as number
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['feed-nudges', clientId] }),
  })
}

export interface PullResult { count: number; batch_id?: string; cached?: boolean; charged?: number }

/**
 * A pull. The morning's five are free and dealt once a day; Pull 5 more adds
 * five for 3 seeds, charged by the function only if something new landed.
 */
export function usePull() {
  const clientId = useEffectiveClientId()
  const userId = useAuthStore((s) => s.user?.id)
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (a: { more: boolean; refresh?: boolean }) => {
      const { data, error } = await supabase.functions.invoke('headlines', {
        body: a.refresh
          ? { client_id: clientId, refresh: true }
          : a.more
            ? { client_id: clientId, more: true, idempotency_key: `pull:${clientId}:${Date.now()}` }
            : { client_id: clientId },
      })
      if (error) {
        const e = await functionError(error)
        if (e.status === 402 || /insufficient seeds/i.test(e.message)) throw new InsufficientSeedsError(e.message)
        throw e
      }
      return data as PullResult
    },
    onSettled: async () => {
      await qc.invalidateQueries({ queryKey: feedKey(clientId, userId) })
      void qc.invalidateQueries({ queryKey: ['seed-balance', clientId] })
    },
  })
}

/** True when nothing has been dealt today yet. */
export const dealtToday = (d: FeedData | undefined) => !!d && d.headlines.some((h) => dayKey(h.fetched_at) === dayKey(Date.now()))

/**
 * The day's first five, pulled when the feed opens and nothing was dealt yet
 * (Brian: "Tomorrow's stories land at 6am"; nothing runs on a timer yet, so
 * the first open of the day deals them). Calls back so the page can show the
 * cards building.
 */
// One try per client per ten minutes, across remounts and both layouts: a pull
// that came back empty must not fire again every time the feed is opened.
const morningTried = new Map<string, number>()

export function useMorningPull(data: FeedData | undefined, isFetched: boolean, start: () => void, skip = false) {
  const clientId = useEffectiveClientId()
  const fired = useRef(false)
  useEffect(() => {
    if (skip || !isFetched || fired.current || !data || !clientId) return
    if (dealtToday(data)) return
    if (Date.now() - (morningTried.get(clientId) ?? 0) < 10 * 60_000) return
    fired.current = true
    morningTried.set(clientId, Date.now())
    start()
  }, [isFetched, data, start, clientId, skip])
}

// ---------------- The feed's URL params ----------------

/**
 * `/app?refresh=mix` (Pull now on Topics): deal today's five again from the
 * saved mix, free, then drop the param. `/app?focus=<headlineId>` (Emma's
 * offers link there): the card to scroll to and open. Both feeds use this.
 */
export function useFeedParams(onRefresh: () => void) {
  const [params, setParams] = useSearchParams()
  const refresh = params.get(FEED_REFRESH_PARAM) === FEED_REFRESH_MIX
  const focus = params.get('focus')
  const done = useRef(false)
  useEffect(() => {
    if (!refresh || done.current) return
    done.current = true
    onRefresh()
    setParams((p) => { p.delete(FEED_REFRESH_PARAM); return p }, { replace: true })
  }, [refresh, onRefresh, setParams])
  const clearFocus = () => setParams((p) => { p.delete('focus'); return p }, { replace: true })
  return { focus, clearFocus, refreshing: refresh }
}

/** Desktop: Send to your phone (the Library builder's story_progress.to_phone_at). */
export function useSendToPhone() {
  const userId = useAuthStore((s) => s.user?.id)
  return useMutation({
    mutationFn: async (storyId: string) => {
      const { error } = await supabase.from('story_progress')
        .upsert({ story_id: storyId, to_phone_at: new Date().toISOString(), to_phone_by: userId ?? null }, { onConflict: 'story_id' })
      if (error) throw error
    },
  })
}

/**
 * A public record's provenance (Brian's records-data.js card(): the record
 * system, the document, and "read · Oct 21"), for a headline built from a
 * Public records find. Null for anything else.
 */
export interface RecordProv { system: string; doc: string; check: string }
export function useRecordProv(headlineId: string | null | undefined, enabled = true) {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['record-prov', clientId, headlineId],
    enabled: !!clientId && !!headlineId && enabled,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<RecordProv | null> => {
      const { data, error } = await supabase.from('record_finds' as never)
        .select('doc_name, doc_checked, source_name, record_sources(name)')
        .eq('client_id', clientId!).eq('headline_id', headlineId!).limit(1).maybeSingle()
      if (error || !data) return null
      const f = data as unknown as { doc_name: string; doc_checked: string | null; source_name: string | null; record_sources: { name: string } | null }
      const when = new Date(f.doc_checked ?? Date.now()).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      return { system: f.record_sources?.name || f.source_name || f.doc_name, doc: f.doc_name, check: `read · ${when}` }
    },
  })
}
